/**
 * Aba Disputa (D98): visão sintética de todos os itens, como na planilha, para competir em vários itens ao mesmo tempo.
 * - Mesmo rascunho e mesmo cálculo da Precificação: margem e preço editáveis na linha, tudo recalcula sozinho.
 * - Depois do resultado (status Resultado, Recurso ou Homologada) abrem os campos de cada item: situação, nosso lance
 *   final, colocação, preço e empresa vencedora (pelo CNPJ). O lance final congela o preço com a margem real (D93).
 * - O resultado geral é calculado pelos itens (12), com as mesmas regras de v_licitacao.
 */
import { useEffect, useState } from "react";
import { useNotify, useRefresh } from "react-admin";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { cnpjValido, formatarCnpj, margemParaPreco, resultadoGeral } from "@sobral/dominio";
import { consultarCnpj } from "../lib/cnpj";
import { formatarMoeda, formatarNumero, formatarPercentual } from "../lib/formato";
import { supabase } from "../lib/supabase";
import type { ItemLicitacao } from "./licitacao-itens";
import { AcaoDaEtapa } from "./licitacao-status";
import {
  CampoPct,
  CampoPreco,
  custoDoRascunho,
  NOME_ORIGEM,
  ParametrosDaLicitacao,
  congelarLance,
  usePrecificacao,
  valorDoTexto,
  type LicitacaoPreco,
} from "./licitacao-precificacao";
import { NOME_RESULTADO } from "./licitacao-resultado";

const SITUACOES = [
  { id: "ganho", nome: "Ganho" },
  { id: "perdido", nome: "Perdido" },
  { id: "fracassado", nome: "Fracassado" },
  { id: "deserto", nome: "Deserto" },
  { id: "cancelado", nome: "Cancelado" },
];

interface ResultadoItem {
  item_id: string;
  situacao: string;
  nosso_lance_final: number | null;
  preco_vencedor: number | null;
  empresa_vencedora: string | null;
  empresa_vencedora_cnpj: string | null;
  colocacao: number | null;
}

const valorTexto = (v: number | null | undefined) => (v === null || v === undefined ? "" : Number(v).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 4 }));

/** Campo de texto que grava ao sair (ou com Enter). */
function CampoAoSair({
  rotulo,
  valor,
  largura,
  desabilitado,
  aoGravar,
  numerico = false,
}: {
  rotulo: string;
  valor: string;
  largura: number;
  desabilitado: boolean;
  aoGravar: (texto: string) => void;
  numerico?: boolean;
}) {
  const [texto, setTexto] = useState(valor);
  useEffect(() => setTexto(valor), [valor]);
  return (
    <TextField
      size="small"
      placeholder={rotulo}
      value={texto}
      disabled={desabilitado}
      onChange={(e) => setTexto(e.target.value)}
      onBlur={() => texto !== valor && aoGravar(texto)}
      onKeyDown={(e) => e.key === "Enter" && (e.target as HTMLInputElement).blur()}
      sx={{ width: largura }}
      slotProps={{ htmlInput: { inputMode: numerico ? "decimal" : "text", "aria-label": rotulo } }}
    />
  );
}

/** Empresa vencedora: digitando um CNPJ, o sistema busca a razão social (D98). */
function CampoEmpresa({ resultado, desabilitado, aoGravar }: { resultado: ResultadoItem | undefined; desabilitado: boolean; aoGravar: (patch: Partial<ResultadoItem>) => void }) {
  const notify = useNotify();
  const [buscando, setBuscando] = useState(false);
  async function gravar(texto: string) {
    const digitos = texto.replace(/\D/g, "");
    if (digitos.length === 14 && cnpjValido(digitos)) {
      setBuscando(true);
      const r = await consultarCnpj(digitos);
      setBuscando(false);
      if (r.situacao === "ok") {
        aoGravar({ empresa_vencedora: r.dados.razao_social ?? texto.trim(), empresa_vencedora_cnpj: digitos });
        return;
      }
      notify(r.situacao === "nao_encontrado" ? "CNPJ não encontrado na Receita." : `Consulta de CNPJ indisponível (${r.detalhe}). Gravei o CNPJ; complete o nome depois.`, {
        type: "warning",
      });
      aoGravar({ empresa_vencedora: resultado?.empresa_vencedora ?? null, empresa_vencedora_cnpj: digitos });
      return;
    }
    aoGravar({ empresa_vencedora: texto.trim() || null });
  }
  return (
    <Box>
      <CampoAoSair rotulo={buscando ? "Consultando…" : "CNPJ ou nome"} valor={resultado?.empresa_vencedora ?? ""} largura={220} desabilitado={desabilitado || buscando} aoGravar={(t) => void gravar(t)} />
      {resultado?.empresa_vencedora_cnpj && (
        <Typography variant="caption" color="text.secondary" display="block">
          {formatarCnpj(resultado.empresa_vencedora_cnpj)}
        </Typography>
      )}
    </Box>
  );
}

export function DisputaLicitacao({ licitacao, status }: { licitacao: LicitacaoPreco; status: string }) {
  const notify = useNotify();
  const refresh = useRefresh();
  const { fiscal, erroFiscal, rascunhos, ordenados, calculos, porItemCongelado, perfis, mudar, alternarParticipar } = usePrecificacao(licitacao);
  const [resultados, setResultados] = useState<Map<string, ResultadoItem> | null>(null);
  const [margens, setMargens] = useState<Record<string, number>>({});
  const comResultado = status === "resultado" || status === "recurso" || status === "homologada";
  const ids = ordenados.map((i) => i.id).join(",");

  useEffect(() => {
    if (!ids) {
      setResultados(new Map());
      return;
    }
    let ativo = true;
    void supabase
      .from("item_resultado")
      .select("item_id, situacao, nosso_lance_final, preco_vencedor, empresa_vencedora, empresa_vencedora_cnpj, colocacao")
      .in("item_id", ids.split(","))
      .then(({ data }) => ativo && setResultados(new Map(((data ?? []) as ResultadoItem[]).map((x) => [x.item_id, x]))));
    return () => {
      ativo = false;
    };
  }, [ids]);

  if (erroFiscal) return <Alert severity="error">Não foi possível carregar os parâmetros de preço: {erroFiscal.message}</Alert>;
  if (!fiscal || !rascunhos || !resultados) return <Typography variant="body2">Carregando…</Typography>;
  if (ordenados.length === 0) return <Alert severity="info">Cadastre os itens na aba Itens.</Alert>;

  async function gravarResultado(item: ItemLicitacao, patch: Partial<ResultadoItem>) {
    const atual = resultados?.get(item.id);
    const novo: ResultadoItem = {
      ...(atual ?? { item_id: item.id, situacao: "", nosso_lance_final: null, preco_vencedor: null, empresa_vencedora: null, empresa_vencedora_cnpj: null, colocacao: null }),
      ...patch,
    };
    if (!novo.situacao) {
      notify("Escolha primeiro a situação do item.", { type: "info" });
      return;
    }
    // Item ganho: o vencedor somos nós.
    if (novo.situacao === "ganho") Object.assign(novo, { preco_vencedor: null, empresa_vencedora: null, empresa_vencedora_cnpj: null, colocacao: 1 });
    setResultados((m) => new Map(m ?? []).set(item.id, novo));
    const { error } = atual
      ? await supabase.from("item_resultado").update(novo).eq("item_id", item.id)
      : await supabase.from("item_resultado").insert(novo);
    if (error) {
      notify(`Não foi possível gravar o resultado: ${error.message}`, { type: "error" });
      return;
    }
    refresh();
    if (patch.nosso_lance_final && fiscal) {
      try {
        const margem = await congelarLance(item, rascunhos?.get(item.id) ?? null, licitacao, fiscal, patch.nosso_lance_final);
        if (margem !== null) setMargens((m) => ({ ...m, [item.id]: margem }));
      } catch (e) {
        notify(`Lance gravado, mas a margem do lance não foi calculada: ${(e as Error).message}`, { type: "warning" });
      }
    }
  }

  function aplicarPreco(item: ItemLicitacao, texto: string) {
    const calc = calculos.get(item.id);
    const alvo = valorDoTexto(texto);
    if (!calc?.contexto || !calc.resultado || alvo === null || Number.isNaN(alvo) || alvo === calc.resultado.preco_unit) return;
    try {
      mudar(item.id, { margem: margemParaPreco(calc.contexto, alvo).margem });
    } catch (e) {
      notify((e as Error).message, { type: "warning" });
    }
  }

  const participando = ordenados.filter((i) => i.participar);
  const proposto = participando.reduce((s, i) => {
    const r = calculos.get(i.id)?.resultado;
    return s + (r ? Math.round(r.preco_unit * Number(i.qtd_max) * 100) / 100 : 0);
  }, 0);
  const estimado = participando.reduce((s, i) => s + Number(i.valor_total_estimado ?? 0), 0);
  const geral = resultadoGeral(participando.map((i) => resultados.get(i.id)?.situacao ?? null));
  const ganho = participando.reduce((s, i) => {
    const r = resultados.get(i.id);
    if (r?.situacao !== "ganho") return s;
    const preco = r.nosso_lance_final ?? porItemCongelado.get(i.id)?.preco_unit ?? calculos.get(i.id)?.resultado?.preco_unit ?? 0;
    return s + Math.round(Number(preco) * Number(i.qtd_max) * 100) / 100;
  }, 0);

  return (
    <Box>
      <ParametrosDaLicitacao licitacao={licitacao} fiscal={fiscal} perfis={perfis} lateral={<AcaoDaEtapa licitacaoId={licitacao.id} status={status} grande />} />
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
        <Chip variant="outlined" label={`Estimado ${formatarMoeda(estimado)}`} />
        <Chip color="primary" label={`Nossa proposta ${formatarMoeda(Math.round(proposto * 100) / 100)}`} />
        {comResultado && geral && <Chip color={geral.startsWith("ganha") ? "success" : geral === "pendente" ? "warning" : "default"} label={NOME_RESULTADO[geral]} />}
        {comResultado && ganho > 0 && <Chip color="success" variant="outlined" label={`Ganho ${formatarMoeda(Math.round(ganho * 100) / 100)}`} />}
      </Stack>
      {comResultado && (geral === null || geral === "pendente") && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          Registre o resultado de cada item disputado: situação, nosso lance final e, se perdido, o vencedor.
        </Alert>
      )}
      <TableContainer sx={{ overflowX: "auto" }}>
        <Table size="small" sx={{ minWidth: comResultado ? 1650 : 1150, "& td, & th": { px: 0.75 } }}>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" />
              <TableCell>Item</TableCell>
              <TableCell>Nosso produto</TableCell>
              <TableCell align="right">Qtd.</TableCell>
              <TableCell align="right">Edital</TableCell>
              <TableCell align="right">Custo</TableCell>
              <TableCell align="right">ICMS</TableCell>
              <TableCell>Margem</TableCell>
              <TableCell>Preço unit.</TableCell>
              <TableCell align="right">Total</TableCell>
              <TableCell align="right">× edital</TableCell>
              {comResultado && (
                <>
                  <TableCell>Situação</TableCell>
                  <TableCell>Lance final</TableCell>
                  <TableCell>Colocação</TableCell>
                  <TableCell>Preço vencedor</TableCell>
                  <TableCell>Empresa vencedora</TableCell>
                </>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {ordenados.map((i) => {
              const r = rascunhos.get(i.id);
              const calc = calculos.get(i.id);
              const res = calc?.resultado ?? null;
              const p = calc?.parametros;
              const edital = i.valor_unit_estimado === null ? null : Number(i.valor_unit_estimado);
              const diferenca = res && edital ? res.preco_unit / edital - 1 : null;
              const ri = resultados.get(i.id);
              const semDisputa = ri?.situacao === "fracassado" || ri?.situacao === "deserto" || ri?.situacao === "cancelado";
              const ganhoItem = ri?.situacao === "ganho";
              return (
                <TableRow key={i.id} hover sx={{ opacity: i.participar ? 1 : 0.5 }}>
                  <TableCell padding="checkbox">
                    <Checkbox size="small" checked={i.participar} onChange={() => void alternarParticipar(i)} inputProps={{ "aria-label": `Participar do item ${i.numero}` }} />
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    {i.lote ? `${i.lote}.` : ""}
                    {i.numero}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 280 }}>
                    <Tooltip title={i.descricao}>
                      <Typography variant="body2" noWrap>
                        {r?.produto_descricao ?? "Sem produto"}
                      </Typography>
                    </Tooltip>
                    <Typography variant="caption" color="text.secondary" noWrap display="block">
                      {r?.fornecedor_nome ? `${r.fornecedor_nome} (${r.fornecedor_uf ?? ""})` : ""}
                    </Typography>
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                    {formatarNumero(i.qtd_max)}
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                    {edital === null ? "—" : formatarMoeda(edital)}
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                    {custoDoRascunho(r) !== null ? formatarMoeda(custoDoRascunho(r)) : "—"}
                  </TableCell>
                  <TableCell align="right">{res ? formatarPercentual(res.icms_aplicado) : "—"}</TableCell>
                  {/* Largura mínima: com a tabela larga (resultado), o navegador espremia os campos e escondia o valor. */}
                  <TableCell sx={{ minWidth: 120 }}>
                    {p && (
                      <CampoPct
                        rotulo="Margem"
                        valor={p.margem}
                        proprio={p.origem.margem === "item"}
                        origem={NOME_ORIGEM[p.origem.margem] ?? ""}
                        compacto
                        aoMudar={(v) => mudar(i.id, { margem: v })}
                      />
                    )}
                  </TableCell>
                  <TableCell sx={{ minWidth: 150 }}>
                    {res ? (
                      <CampoPreco preco={res.preco_unit} aoAplicar={(t) => aplicarPreco(i, t)} />
                    ) : (
                      <Tooltip title={calc?.pendencia ?? ""}>
                        <Typography variant="caption" color="warning.main" sx={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                          {calc?.pendencia ?? "—"}
                        </Typography>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: "nowrap", fontWeight: 700 }}>
                    {res ? formatarMoeda(Math.round(res.preco_unit * Number(i.qtd_max) * 100) / 100) : "—"}
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: "nowrap", color: diferenca === null ? undefined : diferenca > 0 ? "error.main" : "success.main" }}>
                    {diferenca === null ? "—" : `${diferenca > 0 ? "+" : ""}${formatarPercentual(diferenca)}`}
                  </TableCell>
                  {comResultado && (
                    <>
                      <TableCell>
                        <TextField
                          select
                          size="small"
                          value={ri?.situacao ?? ""}
                          onChange={(e) => void gravarResultado(i, { situacao: e.target.value })}
                          sx={{ width: 130 }}
                          slotProps={{ htmlInput: { "aria-label": `Situação do item ${i.numero}` } }}
                        >
                          {SITUACOES.map((s) => (
                            <MenuItem key={s.id} value={s.id}>
                              {s.nome}
                            </MenuItem>
                          ))}
                        </TextField>
                      </TableCell>
                      <TableCell>
                        <CampoAoSair
                          rotulo="R$"
                          numerico
                          largura={120}
                          valor={valorTexto(ri?.nosso_lance_final)}
                          desabilitado={!ri || semDisputa}
                          aoGravar={(t) => {
                            const v = valorDoTexto(t);
                            if (v !== null && (Number.isNaN(v) || v <= 0)) notify("Valor inválido.", { type: "warning" });
                            else void gravarResultado(i, { nosso_lance_final: v });
                          }}
                        />
                        {margens[i.id] !== undefined && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            Margem no lance {formatarPercentual(margens[i.id])}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <CampoAoSair
                          rotulo="Nº"
                          numerico
                          largura={64}
                          valor={ri?.colocacao ? String(ri.colocacao) : ""}
                          desabilitado={!ri || ganhoItem || semDisputa}
                          aoGravar={(t) => {
                            const n = t.trim() ? Number(t) : null;
                            if (n !== null && (!Number.isInteger(n) || n < 1)) notify("Colocação inválida.", { type: "warning" });
                            else void gravarResultado(i, { colocacao: n });
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <CampoAoSair
                          rotulo="R$"
                          numerico
                          largura={120}
                          valor={valorTexto(ri?.preco_vencedor)}
                          desabilitado={!ri || ganhoItem || semDisputa}
                          aoGravar={(t) => {
                            const v = valorDoTexto(t);
                            if (v !== null && (Number.isNaN(v) || v <= 0)) notify("Valor inválido.", { type: "warning" });
                            else void gravarResultado(i, { preco_vencedor: v });
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <CampoEmpresa resultado={ri} desabilitado={!ri || ganhoItem || semDisputa} aoGravar={(patch) => void gravarResultado(i, patch)} />
                      </TableCell>
                    </>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
        Margem e preço mudam o rascunho da precificação (o mesmo da aba Precificação). Os demais percentuais e os fretes do item ficam na aba Precificação.
        {!comResultado && " Os campos de resultado abrem quando o status chega a Resultado (botão “Disputa encerrada”)."}
      </Typography>
    </Box>
  );
}
